import React, { useCallback, useEffect, useState } from "react";
import auditModuleService from "src/services/auditModuleService";
import {
  UseCaseConfig,
  UseCaseVersionSummary,
  UseCaseVersionStatus,
  FIELD_TYPE_LABELS,
} from "../shared/types";
import * as S from "../shared/styles";
import FormPreview from "./FormPreview";

// ----------------------------  Helpers  ----------------------------

const errorDetail = (err: unknown): string => {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : e?.message || "Request failed";
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
};

// ----------------------------  VersionDetail  ----------------------------

// Read-only snapshot of one saved version, shown under its row by "View".
const VersionDetail: React.FC<{ config: UseCaseConfig }> = ({ config }) => {
  const rs = config.review_sampling;
  const fields = [...config.fields].sort((a, b) => a.display_order - b.display_order);
  return (
    <div style={detailPanel}>
      <div style={detailGrid}>
        <span style={detailKey}>Name</span>
        <span>{config.name || "-"}</span>
        <span style={detailKey}>Description</span>
        <span>{config.description || "-"}</span>
        <span style={detailKey}>Review sampling</span>
        <span>
          2nd review {rs?.second_enabled ? rs.second_pct + "%" : "off"} &middot; 3rd review{" "}
          {rs?.third_enabled ? rs.third_pct + "%" : "off"}
        </span>
        <span style={detailKey}>Fields</span>
        <span>
          {fields.length === 0
            ? "None"
            : fields
                .map((f) => f.label + " (" + (FIELD_TYPE_LABELS[f.type] || f.type) + ")")
                .join(", ")}
        </span>
        {(config.allocations || []).length > 0 && (
          <>
            <span style={detailKey}>Auditors</span>
            <span>
              {(config.allocations || []).map((a) => a.login + " " + a.percent + "%").join(", ")}
            </span>
          </>
        )}
      </div>
      <div style={{ ...sectionTitle, marginTop: "12px" }}>Form preview</div>
      <FormPreview config={config} />
    </div>
  );
};

// ----------------------------  VersionsTab  ----------------------------

export interface VersionsTabProps {
  useCaseId: string;
  // False for a new use case that has not been saved yet (no versions exist).
  saved: boolean;
  // Called with the restored config after a successful rollback.
  onRolledBack: (config: UseCaseConfig) => void;
}

const VersionsTab: React.FC<VersionsTabProps> = ({ useCaseId, saved, onRolledBack }) => {
  const [versions, setVersions] = useState<UseCaseVersionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [rollingBack, setRollingBack] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [details, setDetails] = useState<Record<number, UseCaseConfig>>({});
  const [detailLoading, setDetailLoading] = useState<number | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Rollback is blocked server-side while no version is active (use case inactive).
  const hasActive = versions.some((v) => v.status === "Active");

  const toggleView = async (version: number) => {
    if (viewing === version) {
      setViewing(null);
      return;
    }
    setViewing(version);
    setDetailError(null);
    if (details[version]) return;
    setDetailLoading(version);
    try {
      const config = await auditModuleService.getUseCaseVersion(useCaseId, version);
      setDetails((prev) => ({ ...prev, [version]: config }));
    } catch (err) {
      setDetailError("Failed to load v" + version + ": " + errorDetail(err));
    } finally {
      setDetailLoading(null);
    }
  };

  const load = useCallback(() => {
    let active = true;
    setLoading(true);
    setLoadError(null);
    auditModuleService
      .getUseCaseVersions(useCaseId)
      .then((list) => {
        if (!active) return;
        // API returns newest first; sort defensively anyway.
        setVersions([...list].sort((a, b) => b.version - a.version));
      })
      .catch((err) => active && setLoadError(errorDetail(err)))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [useCaseId]);

  useEffect(() => {
    if (!saved || !useCaseId) return;
    return load();
  }, [saved, useCaseId, load]);

  const rollback = async (version: number) => {
    setConfirming(null);
    setRollingBack(version);
    setActionError(null);
    setNotice(null);
    try {
      const restored = await auditModuleService.rollbackUseCaseVersion(useCaseId, version);
      setNotice("Rolled back to v" + version + ". It is now the active version.");
      onRolledBack(restored);
      load();
    } catch (err) {
      setActionError("Rollback to v" + version + " failed: " + errorDetail(err));
    } finally {
      setRollingBack(null);
    }
  };

  if (!saved) {
    return (
      <div style={{ ...S.configCard, maxWidth: "42rem" }}>
        <div style={sectionTitle}>Version History</div>
        <p style={mutedText}>Save the use case to start its version history.</p>
      </div>
    );
  }

  return (
    <div style={{ ...S.configCard, maxWidth: "42rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={sectionTitle}>Version History</div>
        <button style={linkBtn} onClick={() => load()} disabled={loading}>
          Refresh
        </button>
      </div>

      {notice && (
        <div role="status" style={noticeBanner}>
          {notice}
        </div>
      )}
      {actionError && (
        <div role="alert" style={errorBanner}>
          {actionError}
        </div>
      )}

      {!loading && !loadError && versions.length > 0 && !hasActive && (
        <div role="note" style={warnBanner}>
          This use case is inactive. Activate it before rolling back to an earlier version.
        </div>
      )}

      {loading && versions.length === 0 ? (
        <p style={mutedText}>Loading versions...</p>
      ) : loadError ? (
        <div role="alert" style={errorBanner}>
          Failed to load versions: {loadError}{" "}
          <button style={linkBtn} onClick={() => load()}>
            Retry
          </button>
        </div>
      ) : versions.length === 0 ? (
        <p style={mutedText}>No versions found.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {versions.map((v) => {
            const isActive = v.status === "Active";
            const busy = rollingBack !== null;
            return (
              <div key={v.version} data-testid={"version-row-" + v.version}>
                <div style={versionRow}>
                  <span style={{ color: "#374151", fontSize: "0.875rem" }}>
                    v{v.version}
                    <span style={{ ...badgeBase, ...BADGE_STYLES[v.status] }}>{v.status}</span>
                    <span style={{ fontSize: "0.75rem", color: "#6b7280", marginLeft: "8px" }}>
                      {v.fieldCount} field{v.fieldCount === 1 ? "" : "s"}
                      {v.updatedAt && <> &middot; {formatDate(v.updatedAt)}</>}
                      {v.updatedBy && <> &middot; {v.updatedBy}</>}
                    </span>
                  </span>
                  <span style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <button
                      style={linkBtn}
                      aria-expanded={viewing === v.version}
                      onClick={() => toggleView(v.version)}
                    >
                      {viewing === v.version ? "Hide" : "View"}
                    </button>
                    {!isActive &&
                      hasActive &&
                      (rollingBack === v.version ? (
                        <span style={mutedText}>Rolling back...</span>
                      ) : confirming === v.version ? (
                        <span style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <span style={{ fontSize: "0.75rem", color: "#374151" }}>
                            Roll back to v{v.version}?
                          </span>
                          <button style={confirmBtn} onClick={() => rollback(v.version)}>
                            Confirm
                          </button>
                          <button style={linkBtn} onClick={() => setConfirming(null)}>
                            Cancel
                          </button>
                        </span>
                      ) : (
                        <button
                          style={linkBtn}
                          disabled={busy}
                          onClick={() => {
                            setActionError(null);
                            setConfirming(v.version);
                          }}
                        >
                          Roll back to this
                        </button>
                      ))}
                  </span>
                </div>
                {viewing === v.version &&
                  (detailLoading === v.version ? (
                    <p style={{ ...mutedText, padding: "6px 12px" }}>Loading v{v.version}...</p>
                  ) : details[v.version] ? (
                    <VersionDetail config={details[v.version]} />
                  ) : detailError ? (
                    <div role="alert" style={{ ...errorBanner, marginTop: "6px" }}>
                      {detailError}
                    </div>
                  ) : null)}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "8px" }}>
        Rolling back copies a version into a new active version — history is never overwritten. Only
        possible while the use case is active. Unsaved edits in this editor are replaced by the
        restored version.
      </p>
    </div>
  );
};

// ----------------------------  Styles  ----------------------------

const sectionTitle: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
};

const mutedText: React.CSSProperties = { fontSize: "0.75rem", color: "#9ca3af" };

const versionRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#f9fafb",
  borderRadius: "6px",
  padding: "8px 12px",
};

const badgeBase: React.CSSProperties = {
  marginLeft: "8px",
  fontSize: "0.7rem",
  padding: "2px 6px",
  borderRadius: "4px",
};

const BADGE_STYLES: Record<UseCaseVersionStatus, React.CSSProperties> = {
  Active: { background: "#ecfdf5", color: "#065f46" },
  Draft: { background: "#fef3c7", color: "#92400e" },
  Archived: { background: "#f3f4f6", color: "#4b5563" },
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: "0.75rem",
  color: "#0f7b8a",
  padding: 0,
};

const confirmBtn: React.CSSProperties = {
  ...linkBtn,
  fontWeight: 600,
  color: "#b45309",
};

const warnBanner: React.CSSProperties = {
  fontSize: "0.8rem",
  background: "#fef3c7",
  color: "#92400e",
  borderRadius: "6px",
  padding: "6px 10px",
  marginBottom: "8px",
};

const detailPanel: React.CSSProperties = {
  border: "1px solid #e5e7eb",
  borderTop: "none",
  borderRadius: "0 0 6px 6px",
  padding: "12px",
  background: "white",
  fontSize: "0.8rem",
  color: "#374151",
};

const detailGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "8rem 1fr",
  rowGap: "4px",
  columnGap: "12px",
};

const detailKey: React.CSSProperties = { color: "#6b7280" };

const noticeBanner: React.CSSProperties = {
  fontSize: "0.8rem",
  background: "#ecfdf5",
  color: "#065f46",
  borderRadius: "6px",
  padding: "6px 10px",
  marginBottom: "8px",
};

const errorBanner: React.CSSProperties = {
  fontSize: "0.8rem",
  background: "#fee2e2",
  color: "#991b1b",
  borderRadius: "6px",
  padding: "6px 10px",
  marginBottom: "8px",
};

export default VersionsTab;
